// Client-side tree assembly for the Modules view. Parallels the backend
// buildAreaTree (backend/api/knowledge/area-tree.ts) — no code is shared across
// the backend/frontend boundary.

import type { AreaTreeNode, KnowledgeArea } from './knowledgeTypes'

const bySort = (a: AreaTreeNode, b: AreaTreeNode): number =>
  a.area.position - b.area.position || a.area.id - b.area.id

export function buildTree(areas: KnowledgeArea[]): AreaTreeNode[] {
  const byId = new Map<number, AreaTreeNode>()
  for (const area of areas) byId.set(area.id, { area, children: [] })

  const roots: AreaTreeNode[] = []
  for (const node of byId.values()) {
    const parent = node.area.parentId != null ? byId.get(node.area.parentId) : undefined
    if (parent) parent.children.push(node)
    else roots.push(node)
  }

  const sortRec = (nodes: AreaTreeNode[]): void => {
    nodes.sort(bySort)
    for (const n of nodes) sortRec(n.children)
  }
  sortRec(roots)
  return roots
}

export interface VisibleRow {
  area: KnowledgeArea
  depth: number
  hasChildren: boolean
}

/** Flatten the tree to the rows currently visible given the expanded set. */
export function flattenVisible(
  tree: AreaTreeNode[],
  expandedIds: ReadonlySet<number>
): VisibleRow[] {
  const out: VisibleRow[] = []
  const walk = (nodes: AreaTreeNode[], depth: number): void => {
    for (const node of nodes) {
      const hasChildren = node.children.length > 0
      out.push({ area: node.area, depth, hasChildren })
      if (hasChildren && expandedIds.has(node.area.id)) walk(node.children, depth + 1)
    }
  }
  walk(tree, 0)
  return out
}

/** ids of `areaId` and its whole subtree — used to exclude invalid move targets. */
export function subtreeIds(areas: KnowledgeArea[], areaId: number): Set<number> {
  const self = areas.find((a) => a.id === areaId)
  const out = new Set<number>([areaId])
  if (!self) return out
  for (const a of areas) {
    if (a.path.startsWith(self.path)) out.add(a.id)
  }
  return out
}
